<template>
  <v-layout v-if="currentGroup" column class="setup-page px-3">
    <div
      v-for="item in currentGroup.items"
      :id="`setup-item-${item.key}`"
      :key="item.key"
      class="setup-item"
    >
      <v-card
        v-if="item.kind === 'preference'"
        class="setup-card mx-2 mb-4"
        flat
        light
      >
        <v-card-title
          class="prompt-header"
          :class="{ 'prompt-header--centered': item.panel.input === 'geolocation' }"
        >
          <mrx-translation :token="preferencePrompt(item.panel)" />
        </v-card-title>

        <v-card-text class="px-0 pb-3">
          <div
            v-if="showInputGuidance(item.panel)"
            class="input-guidance px-5 pb-2"
          >
            <mrx-translation :token="`questions.${item.panel.name}.error`" />
          </div>

          <component
            :is="componentFor(item.panel)"
            :items="item.panel.responses"
            :options="item.panel.options || {}"
            :panel-name="item.panel.name"
            :panel-type="item.panel.type"
            :disabled="Boolean(item.panel.options && item.panel.options.disabled)"
            :agree-progresses-panel="false"
            :embedded="true"
            @accepted="onTermsAccepted"
          />
        </v-card-text>
      </v-card>

      <v-card
        v-else
        class="setup-card mx-2 mb-4"
        flat
        light
      >
        <v-card-title class="prompt-header">
          {{ siteTitle }}
        </v-card-title>

        <v-card-text class="px-5 pb-4">
          <div class="site-search">
            <v-menu
              v-model="siteSearchOpen"
              :close-on-content-click="false"
              :max-height="240"
              :min-width="siteSearchWidth || undefined"
              :max-width="siteSearchWidth || undefined"
              :z-index="10"
              offset-y
              offset-overflow
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  ref="siteSearchInput"
                  id="site-search"
                  v-model="siteQuery"
                  :placeholder="$t('app.inputs.search.title')"
                  outlined
                  dense
                  hide-details="auto"
                  v-on="on"
                  @focus="syncSiteSearchWidth"
                  @input="openSiteSearch"
                />
              </template>

              <v-list
                v-if="siteMatches.length"
                id="site-search-results"
                class="site-search-results pa-0"
              >
                <v-list-item
                  v-for="result in siteMatches"
                  :id="`site-result-${result.site.id || result.site.name}`"
                  :key="result.key"
                  :class="{ 'selected-item': isSelectedSite(result) }"
                  @mousedown.prevent
                  @click="selectSiteResult(result)"
                >
                  <v-list-item-content>
                    <v-list-item-title class="text-wrap">
                      {{ locationLabel(result.site) }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-wrap">
                      {{ ancestorPath(result.ancestors) }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>

              <div v-else-if="siteQuery" class="site-search-no-results">
                {{ $t('app.setup.site_search.no_results') }}
              </div>
            </v-menu>
          </div>
        </v-card-text>
      </v-card>
    </div>
  </v-layout>
</template>

<script>
import GeoLocation from '@/components/inputs/GeoLocation'
import MultiSelect from '@/components/inputs/MultiSelect'
import Terms from '@/components/inputs/Terms'
import TextInput from '@/components/inputs/TextInput'
import { mapActions, mapState } from 'pinia'
import { useLaunchSetupStore } from '@/stores/LaunchSetup'
import {
  filterSites,
  locationToken,
  siteIsSelected,
  useLocationStore,
} from '@/stores/location'

export default {
  name: 'LaunchSetup',
  components: {
    geolocation: GeoLocation,
    multiselect: MultiSelect,
    terms: Terms,
    textinput: TextInput,
  },
  data() {
    return {
      siteQuery: '',
      siteSearchOpen: false,
      siteSearchWidth: 0,
    }
  },
  computed: {
    ...mapState(useLaunchSetupStore, [
      'configurationReady',
      'groups',
      'groupById',
    ]),
    ...mapState(useLocationStore, {
      selectedLocation: 'current',
      allSites: 'sites',
    }),
    currentGroup() {
      return this.groupById(this.$route.params.group)
    },
    siteTitle() {
      const site = this.allSites[0]?.site
      const type = site?.type || 'site'
      return this.$t(`questions.location.prompt.${type}`)
    },
    siteMatches() {
      return filterSites(
        this.allSites,
        this.siteQuery,
        location => this.locationLabel(location),
        this.$i18n.locale,
      )
    },
  },
  methods: {
    ...mapActions(useLocationStore, ['selectSite']),
    ...mapActions(useLaunchSetupStore, { launchSetupNextRoute: 'nextRoute' }),
    componentFor(panel) {
      if (panel.input === 'terms') {
        return 'terms'
      }
      return panel.input
    },
    preferencePrompt(panel) {
      const prompt = `questions.${panel.name}.prompt`
      return this.$te(prompt, this.$i18n.fallbackLocale)
        ? prompt
        : `questions.${panel.name}.title`
    },
    showInputGuidance(panel) {
      return panel.input === 'textinput' &&
        Boolean(panel.options?.inputValidation) &&
        this.$te(`questions.${panel.name}.error`, this.$i18n.fallbackLocale)
    },
    locationLabel(location) {
      const token = location.token || locationToken(location)
      const translated = this.$t(token)
      return translated === token
        ? (location.description || location.name)
        : translated
    },
    ancestorPath(ancestors) {
      return ancestors.map((location) => {
        const level = this.$t(`questions.location.prompt.${location.type}`)
        return `${level}: ${this.locationLabel(location)}`
      }).join(' · ')
    },
    selectSiteResult(result) {
      this.selectSite(result)
      this.siteQuery = this.locationLabel(result.site)
      this.siteSearchOpen = false
    },
    syncSiteSearchWidth() {
      this.$nextTick(() => {
        const input = Array.isArray(this.$refs.siteSearchInput)
          ? this.$refs.siteSearchInput[0]
          : this.$refs.siteSearchInput
        const element = input?.$el || input
        this.siteSearchWidth = Math.round(element?.getBoundingClientRect().width || 0)
      })
    },
    openSiteSearch() {
      this.siteSearchOpen = true
      this.syncSiteSearchWidth()
    },
    isSelectedSite(result) {
      return siteIsSelected(this.selectedLocation, result)
    },
    onTermsAccepted() {
      const groupId = this.currentGroup?.id
      if (groupId !== 'legal') return

      this.$nextTick(() => {
        this.$router.push(this.launchSetupNextRoute(groupId, {
          returnToSummary: Boolean(this.$route.query.focus),
        }))
      })
    },
    syncSelectedSiteQuery() {
      const hasSite = this.currentGroup?.items.some(item => item.kind === 'site')
      if (hasSite && this.selectedLocation) {
        this.siteQuery = this.locationLabel(this.selectedLocation)
      }
    },
    resolveRoute() {
      if (!this.configurationReady) return
      if (!this.$route.params.group || !this.currentGroup) {
        const store = useLaunchSetupStore()
        this.$router.replace(store.entryRoute).catch((error) => {
          if (error.name !== 'NavigationDuplicated') throw error
        })
        return
      }

      this.syncSelectedSiteQuery()
      this.$nextTick(() => {
        const focus = this.$route.query.focus
        if (!focus) return
        document.getElementById(`setup-item-${focus}`)?.scrollIntoView({ block: 'center' })
      })
    },
  },
  watch: {
    configurationReady() {
      this.resolveRoute()
    },
    '$route.params.group'() {
      this.siteQuery = ''
      this.siteSearchOpen = false
      this.siteSearchWidth = 0
      this.resolveRoute()
    },
    '$route.query.focus'() {
      this.resolveRoute()
    },
  },
  mounted() {
    this.resolveRoute()
  },
}
</script>

<style src="./styles/launch-setup.css" scoped></style>
